import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useLocalization} from "../../../../../../hooks/localization";
import { useRPC } from '../../../../../../hooks/rpc'; 
import { useQuestion } from '../../../../../../hooks/question';
import { useMessage } from '../../../../../../hooks/message';
import { PasswordContainer } from '../../../../../../components/PasswordContainer';
import { SettingsPageContainer } from '../../../../../../components/Settings/SettingsPageContainer';
import { SettingInputComponent } from '../../../../../../components/Settings/SettingInputComponent';
import { SettingSelectComponent } from '../../../../../../components/Settings/SettingSelectComponent';
import { useKeyboard } from '../../../../../../hooks/keyboard';
import { SelectItem } from '../../../../../../components/Select';
import { MenuEntity } from '../../../../../../store/control/state';
import { Configs } from '../../../../../../configs';


const BACK_URL = '/settings/custom-menu/sorting-menu/left-menu';

type Icon = {
    name: string;
    iconUrl: string;
    canSpecial?: boolean;
}

export const leftMenuIconList = [
    {
        label: 'intercom',
        value: {
            name: 'intercom',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/intercom.png`,
            canSpecial: true,
        },
    },
    {
        label: 'left_menu_title.energy',
        value: {
            name: 'energy',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/energy.png`,
            canSpecial: true,
        },
    },
    {
        label: 'scenario',
        value: {
            name: 'scenario',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/scenario.png`,
        },
    },
    {
        label: 'area',
        value: {
            name: 'area',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/area.png`,
        },
    },
    {
        label: 'controls',
        value: {
            name: 'all-controls',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/all-controls.png`,
        },
    },
    {
        label: 'media',
        value: {
            name: 'media',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/media.png`,
        },
    },
    {
        label: 'alarm',
        value: {
            name: 'alarm',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/alarm.png`,
            canSpecial: true,
        },
    },
    {
        label: 'concierge',
        value: {
            name: 'concierge',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/concierge.png`,
            canSpecial: true,
        },
    },
    {
        label: 'sensor',
        value: {
            name: 'sensor',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/sensor.png`,
        },
    },
    {
        label: 'curtain',
        value: {
            name: 'curtain',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/curtain.png`,
        },
    },
    {
        label: 'lamp',
        value: {
            name: 'lamp',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/lamp.png`,
        },
    },
    {
        label: 'plug',
        value: {
            name: 'plug',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/plug.png`,
        },
    },
    {
        label: 'air_conditioning',
        value: {
            name: 'airConditioner',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/air-conditioner.png`,
        },
    },
    {
        label: 'camera',
        value: {
            name: 'camera',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/camera.png`,
            canSpecial: true,
        },
    },
    {
        label: 'contactor',
        value: {
            name: 'contactor',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/contactor.png`,
        },
    },
    {
        label: 'heating',
        value: {
            name: 'temperature',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/temperature.png`,
        },
    },
    {
        label: 'fancoil',
        value: {
            name: 'fancoil',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/fancoil.png`,
        },
    },
    {
        label: 'settings_2',
        value: {
            name: 'settings',
            iconUrl: `${Configs.IMAGE_PREFIX}/images/menu-icons/settings.png`,
            canSpecial: true,
        },
    },
];

export const LeftMenuEditScreen = memo(function LeftMenuEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [rpc] = useRPC();
    const question = useQuestion();
    const {success, failure} = useMessage();
    const history = useHistory();

    const menuList = useSelector(state => state.control.menuList);
    const menuIconListFromState = useSelector(state => state.control.menuIconList);
    const [selectedMenuItem, setSelectedMenuItem] = useState<MenuEntity>(null);

    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [menuItemName, setMenuItemName] = useState<string>('');
    const [menuIcon, setMenuIcon] = useState<SelectItem<Icon>>(null);
    const order = menuList.length === 0 ? 0 : menuList.sort((a, b) => b.order - a.order)[0].order + 1;

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_menu'), () => {
            return rpc.deleteMenu(selectedMenuItem.id)
                .then(() => {
                    success(t('menu_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, failure, onBackPressed, question, rpc, selectedMenuItem, success]);

    const isFieldsValid = useCallback((): boolean => {
        if (menuItemName == null || menuItemName.length === 0 || menuItemName.length > 30) {
            failure(t('pls_enter_a_valid_name'));

            return false;
        }

        if (menuIcon == null) {
            failure(t('pls_select_a_icon'));

            return false;
        }

        return true;
    }, [t, failure, menuItemName, menuIcon]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const selectedIconName = menuIcon?.value?.name;
        const matchedIcon = (menuIconListFromState || []).find(i => i.icon === selectedIconName);
        const url = matchedIcon?.url || selectedMenuItem?.url || '';

        const menuEntity: MenuEntity = {
            id: selectedMenuItem != null ? selectedMenuItem.id : null,
            icon: selectedIconName,
            name: menuItemName,
            order: selectedMenuItem != null ? selectedMenuItem.order : order,
            content_list: selectedMenuItem?.content_list || [],
            is_special: selectedMenuItem?.is_special || false,
            is_hidden: selectedMenuItem?.is_hidden || false,
            url,
        };

        return rpc.updateMenu(menuEntity)
            .then(() => {
                success(t(menuEntity.id == null ? 'menu_successfully_created' : 'menu_successfully_edited'));
                onBackPressed();
            }).catch(failure);
    }, [t, isFieldsValid, menuIcon?.value?.name, selectedMenuItem, menuItemName, order, rpc, failure, success, onBackPressed, menuIconListFromState]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const buttons = useMemo(() =>
        [
            {
                text: t('delete'),
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selectedMenuItem) || selectedMenuItem.is_special,
            },
            {
                text: t('save'),
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: t('cancel'),
                className: 'red',
                url: BACK_URL,
            },
        ], [t, onDeletePressed, onSavePressed, selectedMenuItem],
    );

    const translatedLeftMenuIconList = useMemo(() => leftMenuIconList.map(icon => ({...icon, label: t(icon.label)})), [t]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const item = menuList.find((menuItem) => menuItem.id === params.id);

        if (item === null) {
            return;
        }

        const icon = translatedLeftMenuIconList.find((icon) => icon?.value?.name === item?.icon);

        setSelectedMenuItem(item);
        setMenuItemName(item?.name);
        setMenuIcon(icon || null);
    }, [menuList, params.id, translatedLeftMenuIconList]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            label={t("menu_name")}
                            placeholder={t("write_menu_name")}
                            value={menuItemName}
                            setValue={setMenuItemName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        {
                            renderIf(!selectedMenuItem?.is_special)(() => (
                                <SettingSelectComponent
                                    label={t('menu_icon')}
                                    options={translatedLeftMenuIconList}
                                    onChange={setMenuIcon}
                                    value={menuIcon}/>
                            ))
                        }
                    </div>
                    <div className='content-wrapper'></div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
